import { useEffect, useMemo } from 'react';

import { gql } from '@apollo/client';

import {
  DefaultTaskAssigneeQuery,
  Permission,
  TaskDefaultAssignee,
  useCoordinatorRecruiterUserInfosQuery,
  useDefaultTaskAssigneeQuery,
} from 'src/generated/mloop-graphql';

import { OrgPrefName } from 'src/utils/api/org';

/**
 * This is a reusable hook that fetches the default task assignee based on the org preference.
 * @param applicationId - the current application
 * @param prefName - the org pref to use
 * @returns - the default assignee for the given application and org pref
 */
const useDefaultTaskAssignee = ({
  applicationId,
  prefName,
  onCompleted,
}: {
  applicationId: string;
  prefName: OrgPrefName;
  onCompleted?: (
    data: Exclude<DefaultTaskAssigneeQuery['application'], null | undefined>['recruiter'] | null | undefined
  ) => void;
}) => {
  const {
    data: appData,
    loading: appLoading,
    error: appError,
  } = useDefaultTaskAssigneeQuery({
    variables: {
      applicationId,
      prefName,
    },
  });

  const userIdsToFetch = useMemo(() => {
    return [appData?.application?.coordinator?.id, appData?.application?.recruiter?.id].filter(Boolean) as string[];
  }, [appData]);

  const {
    data: userInfosData,
    loading: uiLoading,
    error: uiError,
  } = useCoordinatorRecruiterUserInfosQuery({
    variables: {
      ids: userIdsToFetch,
    },
    skip: userIdsToFetch.length === 0,
  });

  const assignee = useMemo(() => {
    if (!appData) {
      return null;
    }

    const pref = appData?.thisOrg?.orgPref?.stringValue;

    // If the org pref is set to candidate recruiter, return recruiter if they are a user with scheduling ability in ModernLoop
    if (pref === TaskDefaultAssignee.CandidateRecruiter) {
      const recruiterId = appData?.application?.recruiter?.id;
      const recruiterUser = userInfosData?.userInfoByEmployeeIds?.find(
        (userInfo) => userInfo.employeeId === recruiterId
      );
      const recruiterPermissions = recruiterUser?.permissions ?? [];
      const recruiterCanSchedule = recruiterPermissions.includes(Permission.CanSchedule);

      if (recruiterCanSchedule) {
        return appData?.application?.recruiter;
      }

      return null;
    }

    // If the org pref is set to candidate coordinator, return coordinator if they are a user with scheduling ability in ModernLoop
    if (pref === TaskDefaultAssignee.CandidateCoordinator) {
      const coordinatorId = appData?.application?.coordinator?.id;
      const coordinatorUser = userInfosData?.userInfoByEmployeeIds?.find(
        (userInfo) => userInfo.employeeId === coordinatorId
      );
      const coordinatorPermissions = coordinatorUser?.permissions ?? [];
      const coordinatorCanSchedule = coordinatorPermissions.includes(Permission.CanSchedule);

      if (coordinatorCanSchedule) {
        return appData?.application?.coordinator;
      }

      return null;
    }

    if (pref === TaskDefaultAssignee.TaskCreator) {
      return appData?.thisEmployee;
    }

    return null;
  }, [appData, userInfosData]);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect intentionally performs a side effect after render; revisit before adding logic.
  useEffect(() => {
    if (assignee && onCompleted) {
      onCompleted(assignee);
    }
  }, [assignee, onCompleted]);

  return {
    assignee,
    loading: appLoading || uiLoading,
    error: appError || uiError,
  };
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const DEFAULT_TASK_ASSIGNEE_QUERY = gql`
  query DefaultTaskAssignee($applicationId: uuid!, $prefName: String!) {
    application(id: $applicationId) {
      coordinator {
        id
        fullName
        internalProfilePictureUrl
      }
      recruiter {
        id
        fullName
        internalProfilePictureUrl
      }
    }
    thisOrg {
      id
      orgPref(prefName: $prefName) {
        id
        orgId
        prefName
        stringValue
      }
    }
    thisEmployee {
      id
      fullName
      internalProfilePictureUrl
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const COORDINATOR_RECRUITER_USER_INFOS_QUERY = gql`
  query CoordinatorRecruiterUserInfos($ids: [uuid!]!) {
    userInfoByEmployeeIds(ids: $ids) {
      userId
      employeeId
      permissions
    }
  }
`;

export default useDefaultTaskAssignee;
